import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { MappoolService, AvailableMap, SeasonMappool } from '../../services/MappoolService';
import { seasonSettingsService, SeasonSettings } from '../../services/seasonSettingsService';
import '../../styles/admin/admin-settings-page.scss';

const AdminSettingsPage: React.FC = () => {
    const { token, isAdmin } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState<'mappool' | 'general'>('mappool');

    // Mappool state
    const [availableMaps, setAvailableMaps] = useState<AvailableMap[]>([]);
    const [allMappools, setAllMappools] = useState<SeasonMappool[]>([]);
    const [selectedSeason, setSelectedSeason] = useState<number>(3);
    const [selectedMaps, setSelectedMaps] = useState<string[]>([]);
    const [mappoolName, setMappoolName] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // General Settings - Season Settings state
    const [allSeasonSettings, setAllSeasonSettings] = useState<SeasonSettings[]>([]);
    const [generalSettingsSeason, setGeneralSettingsSeason] = useState<number>(3);
    const [groupsVisible, setGroupsVisible] = useState<boolean>(false);
    const [announcementDate, setAnnouncementDate] = useState<string>('');
    const [generalLoading, setGeneralLoading] = useState<boolean>(false);
    const [generalSaving, setGeneralSaving] = useState<boolean>(false);
    const [generalMessage, setGeneralMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Set page title
    useEffect(() => {
        document.title = 'Settings | Northern Championships Series';
    }, []);

    // Load available maps and existing mappools on mount (only if authenticated)
    useEffect(() => {
        if (token && isAdmin) {
            loadData();
        } else {
            setLoading(false);
            setMessage({ type: 'error', text: 'You must be logged in as an admin to access this page.' });
        }
    }, [token, isAdmin]);

    // Load selected season's mappool when season changes
    useEffect(() => {
        if (allMappools.length > 0) {
            loadSeasonMappool();
        }
    }, [selectedSeason, allMappools]);

    const loadData = async () => {
        if (!token) return;

        try {
            setLoading(true);
            const [maps, mappools] = await Promise.all([
                MappoolService.fetchAvailableMaps(token),
                MappoolService.fetchAllMappools(token)
            ]);

            setAvailableMaps(maps);
            setAllMappools(mappools);
            setLoading(false);
        } catch (error: any) {
            console.error('Error loading settings data:', error);

            // Check if this is a 403 Forbidden error
            if (error.response?.status === 403) {
                setMessage({ type: 'error', text: 'You don\'t have permission to access admin settings' });
            } else {
                setMessage({ type: 'error', text: 'Failed to load settings data' });
            }

            setLoading(false);
        }
    };

    const loadSeasonMappool = () => {
        const mappool = allMappools.find(m => m.season === selectedSeason);

        if (mappool) {
            setSelectedMaps(mappool.maps);
            setMappoolName(mappool.name);
            setIsActive(mappool.isActive);
        } else {
            // New season - set defaults
            setSelectedMaps([]);
            setMappoolName(`Season ${selectedSeason} - Northern Championship Series`);
            setIsActive(false);
        }
    };

    const toggleMapSelection = (mapName: string) => {
        if (selectedMaps.includes(mapName)) {
            setSelectedMaps(selectedMaps.filter(m => m !== mapName));
        } else {
            setSelectedMaps([...selectedMaps, mapName]);
        }
    };

    const handleSave = async () => {
        if (!token) {
            setMessage({ type: 'error', text: 'Not authenticated' });
            return;
        }

        if (selectedMaps.length < 5) {
            setMessage({ type: 'error', text: 'Please select at least 5 maps' });
            return;
        }

        if (selectedMaps.length > 9) {
            setMessage({ type: 'error', text: 'Please select no more than 9 maps' });
            return;
        }

        try {
            setSaving(true);
            setMessage(null);

            await MappoolService.updateSeasonMappool(
                selectedSeason,
                {
                    season: selectedSeason,
                    name: mappoolName,
                    maps: selectedMaps,
                    isActive
                },
                token
            );

            setMessage({ type: 'success', text: `Season ${selectedSeason} mappool saved successfully!` });

            // Reload data to get updated mappools
            await loadData();
        } catch (error: any) {
            console.error('Error saving mappool:', error);

            // Check if this is a 403 Forbidden error
            if (error.response?.status === 403) {
                setMessage({ type: 'error', text: 'You don\'t have permission to update mappools' });
            } else {
                const errorMessage = error.response?.data?.message || 'Failed to save mappool';
                setMessage({ type: 'error', text: errorMessage });
            }
        } finally {
            setSaving(false);
        }
    };

    // Load all season settings (for General Settings tab)
    const loadSeasonSettings = async () => {
        if (!token) return;

        try {
            setGeneralLoading(true);
            const settings = await seasonSettingsService.getAllSeasonSettings(token);
            setAllSeasonSettings(settings);
            setGeneralLoading(false);

            // Load the selected season's settings
            loadGeneralSeasonSettings();
        } catch (error: any) {
            console.error('Error loading season settings:', error);

            if (error.response?.status === 403) {
                setGeneralMessage({ type: 'error', text: 'You don\'t have permission to access season settings' });
            } else {
                setGeneralMessage({ type: 'error', text: 'Failed to load season settings' });
            }

            setGeneralLoading(false);
        }
    };

    // Load specific season's settings
    const loadGeneralSeasonSettings = () => {
        const settings = allSeasonSettings.find(s => s.season === generalSettingsSeason);

        if (settings) {
            setGroupsVisible(settings.groupsVisible);
            setAnnouncementDate(settings.announcementDate ? settings.announcementDate.substring(0, 16) : '');
        } else {
            // New season - set defaults
            setGroupsVisible(false);
            setAnnouncementDate('');
        }
    };

    // Load season settings when General Settings tab is active
    useEffect(() => {
        if (activeTab === 'general' && token && isAdmin && allSeasonSettings.length === 0) {
            loadSeasonSettings();
        }
    }, [activeTab, token, isAdmin]);

    // Load selected season's settings when season changes
    useEffect(() => {
        if (allSeasonSettings.length > 0) {
            loadGeneralSeasonSettings();
        }
    }, [generalSettingsSeason, allSeasonSettings]);

    // Handle saving general settings
    const handleGeneralSave = async () => {
        if (!token) {
            setGeneralMessage({ type: 'error', text: 'Not authenticated' });
            return;
        }

        try {
            setGeneralSaving(true);
            setGeneralMessage(null);

            await seasonSettingsService.updateSeasonSettings(
                generalSettingsSeason,
                {
                    groupsVisible,
                    announcementDate: announcementDate || null
                },
                token
            );

            setGeneralMessage({
                type: 'success',
                text: `Season ${generalSettingsSeason} settings saved successfully!`
            });

            // Reload season settings
            await loadSeasonSettings();
        } catch (error: any) {
            console.error('Error saving season settings:', error);

            if (error.response?.status === 403) {
                setGeneralMessage({ type: 'error', text: 'You don\'t have permission to update settings' });
            } else {
                const errorMessage = error.response?.data?.message || 'Failed to save settings';
                setGeneralMessage({ type: 'error', text: errorMessage });
            }
        } finally {
            setGeneralSaving(false);
        }
    };

    return (
        <div className="admin-settings-page">
            <div className="container">
                <div className="admin-header">
                    <h1>Site Settings</h1>
                </div>

                {/* Tab Navigation */}
                <div className="settings-tabs">
                    <button
                        className={`tab ${activeTab === 'mappool' ? 'active' : ''}`}
                        onClick={() => setActiveTab('mappool')}
                    >
                        Mappool Configuration
                    </button>
                    <button
                        className={`tab ${activeTab === 'general' ? 'active' : ''}`}
                        onClick={() => setActiveTab('general')}
                    >
                        General Settings
                    </button>
                </div>

                {/* Mappool Configuration Tab */}
                {activeTab === 'mappool' && (
                    <div className="settings-content">
                        {loading ? (
                            <div className="loading">Loading mappool data...</div>
                        ) : (
                            <>
                                <div className="settings-section">
                                    <h2>Season Mappool Configuration</h2>
                                    <p className="description">
                                        Configure the active competitive mappool for each season. This mappool will be used for veto processes
                                        and match creation to ensure consistency across the tournament.
                                    </p>

                                    {/* Season Selector */}
                                    <div className="form-group">
                                        <label htmlFor="season-select">Select Season</label>
                                        <select
                                            id="season-select"
                                            value={selectedSeason}
                                            onChange={(e) => setSelectedSeason(Number(e.target.value))}
                                            className="season-select"
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(season => (
                                                <option key={season} value={season}>
                                                    Season {season}
                                                    {allMappools.find(m => m.season === season)?.isActive && ' (Active)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Mappool Name */}
                                    <div className="form-group">
                                        <label htmlFor="mappool-name">Mappool Name</label>
                                        <input
                                            type="text"
                                            id="mappool-name"
                                            value={mappoolName}
                                            onChange={(e) => setMappoolName(e.target.value)}
                                            placeholder="e.g., Season 3 - Winter 2025"
                                            className="mappool-name-input"
                                        />
                                    </div>

                                    {/* Is Active Checkbox */}
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={(e) => setIsActive(e.target.checked)}
                                            />
                                            <span>Set as active mappool</span>
                                        </label>
                                        <p className="checkbox-help">
                                            Only one mappool can be active at a time. Setting this as active will deactivate other seasons.
                                        </p>
                                    </div>

                                    {/* Map Selection */}
                                    <div className="map-selection-section">
                                        <h3>Select Maps ({selectedMaps.length} selected)</h3>
                                        <p className="selection-note">Select 5-9 maps for the competitive mappool</p>

                                        {/* Available Maps */}
                                        <div className="map-category">
                                            <h4>Available Maps</h4>
                                            <div className="map-grid">
                                                {availableMaps.map(map => (
                                                    <div
                                                        key={map.id}
                                                        className={`map-card ${selectedMaps.includes(map.mapName) ? 'selected' : ''}`}
                                                        onClick={() => toggleMapSelection(map.mapName)}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMapSelection(map.mapName); }}
                                                    >
                                                        <div className="map-image">
                                                            <img src={map.imageUrl} alt={map.mapName} />
                                                        </div>
                                                        <div className="map-name">{map.mapName}</div>
                                                        <div className="map-checkbox">
                                                            {selectedMaps.includes(map.mapName) && <span>✓</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Message Display */}
                                    {message && (
                                        <div className={`message ${message.type}`}>
                                            {message.text}
                                        </div>
                                    )}

                                    {/* Save Button */}
                                    <div className="form-actions">
                                        <button
                                            onClick={handleSave}
                                            disabled={saving || selectedMaps.length < 5 || selectedMaps.length > 9}
                                            className="btn-save"
                                        >
                                            {saving ? 'Saving...' : 'Save Configuration'}
                                        </button>
                                    </div>
                                </div>

                                {/* Historical Mappools */}
                                <div className="settings-section">
                                    <h3>Historical Mappools</h3>
                                    <div className="historical-mappools">
                                        {allMappools.length === 0 ? (
                                            <p>No mappools configured yet.</p>
                                        ) : (
                                            <table className="mappool-table">
                                                <thead>
                                                    <tr>
                                                        <th>Season</th>
                                                        <th>Name</th>
                                                        <th>Maps</th>
                                                        <th>Status</th>
                                                        <th>Last Updated</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allMappools.map(mappool => (
                                                        <tr key={mappool.id}>
                                                            <td>{mappool.season}</td>
                                                            <td>{mappool.name}</td>
                                                            <td className="maps-cell">{mappool.maps.join(', ')}</td>
                                                            <td>
                                                                {mappool.isActive && <span className="badge active">Active</span>}
                                                            </td>
                                                            <td>{new Date(mappool.updatedAt).toLocaleDateString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* General Settings Tab */}
                {activeTab === 'general' && (
                    <div className="settings-content">
                        {generalLoading ? (
                            <div className="loading">Loading season settings...</div>
                        ) : (
                            <>
                                <div className="settings-section">
                                    <h2>Season Groups Visibility</h2>
                                    <p className="description">
                                        Control when groups are visible to the public for each season. When groups are hidden,
                                        users will see a message indicating that groups will be announced soon.
                                    </p>

                                    {/* Season Selector */}
                                    <div className="form-group">
                                        <label htmlFor="general-season-select">Select Season</label>
                                        <select
                                            id="general-season-select"
                                            value={generalSettingsSeason}
                                            onChange={(e) => setGeneralSettingsSeason(Number(e.target.value))}
                                            className="season-select"
                                        >
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(season => (
                                                <option key={season} value={season}>
                                                    Season {season}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Groups Visibility Toggle */}
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={groupsVisible}
                                                onChange={(e) => setGroupsVisible(e.target.checked)}
                                            />
                                            <span>Groups are visible to the public</span>
                                        </label>
                                        <p className="checkbox-help">
                                            When unchecked, groups will be hidden from the public and a "coming soon" message will be displayed.
                                        </p>
                                    </div>

                                    {/* Announcement Date (Optional) */}
                                    <div className="form-group">
                                        <label htmlFor="announcement-date">
                                            Announcement Date (Optional)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="announcement-date"
                                            value={announcementDate}
                                            onChange={(e) => setAnnouncementDate(e.target.value)}
                                            className="datetime-input"
                                        />
                                        <p className="field-help">
                                            If set, a countdown timer will be shown to users indicating when groups will be announced.
                                            Leave empty to show a generic "coming soon" message.
                                        </p>
                                    </div>

                                    {/* Message Display */}
                                    {generalMessage && (
                                        <div className={`message ${generalMessage.type}`}>
                                            {generalMessage.text}
                                        </div>
                                    )}

                                    {/* Save Button */}
                                    <div className="form-actions">
                                        <button
                                            onClick={handleGeneralSave}
                                            disabled={generalSaving}
                                            className="btn-save"
                                        >
                                            {generalSaving ? 'Saving...' : 'Save Settings'}
                                        </button>
                                    </div>
                                </div>

                                {/* All Season Settings Table */}
                                <div className="settings-section">
                                    <h3>All Season Settings</h3>
                                    <div className="season-settings-table">
                                        {allSeasonSettings.length === 0 ? (
                                            <p>No season settings configured yet.</p>
                                        ) : (
                                            <table className="mappool-table">
                                                <thead>
                                                    <tr>
                                                        <th>Season</th>
                                                        <th>Groups Visible</th>
                                                        <th>Announcement Date</th>
                                                        <th>Last Updated</th>
                                                        <th>Updated By</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allSeasonSettings
                                                        .sort((a, b) => b.season - a.season)
                                                        .map(settings => (
                                                            <tr key={settings.id}>
                                                                <td>Season {settings.season}</td>
                                                                <td>
                                                                    {settings.groupsVisible ? (
                                                                        <span className="badge active">Visible</span>
                                                                    ) : (
                                                                        <span className="badge inactive">Hidden</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {settings.announcementDate
                                                                        ? new Date(settings.announcementDate).toLocaleString()
                                                                        : 'Not set'}
                                                                </td>
                                                                <td>{new Date(settings.updatedAt).toLocaleString()}</td>
                                                                <td>{settings.updatedBy || 'System'}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSettingsPage;
